export function buildPath(path: string, params: Record<string, string>) {
    return Object.entries(params).reduce(
        (acc, [key, value]) => acc.replace(`:${key}`, value),
        path
    );
}

const ROOTS = {
    MANAGE: '/manage',
}


export const paths = {
    signIn: '/sign-in',
    signUp: '/sign-up',
    public: {
        root: '/',
        form: '/form/:token',
        events: {
            details: '/events/:id',
            register_root: '/events/:id/*',
            register: '/events/:id/register',
            addTickets: '/events/:id/add-tickets',
            payment: '/events/:id/payment',
            confirmation: '/events/:id/confirmation',
        },
        embed: {
            events: '/embed/events',
            eventDetails: '/embed/event/:id',
            eventRegister: '/embed/event/:id/register',
            eventPayment: '/embed/event/:id/payment',
            eventConfirmation: '/embed/event/:id/confirmation',
        },
        searchEvents: `/events`,
        search: '/search',
        eventOrganizers: `/organisers`,
        eventOrganizerProfile: `/organisers/:companyKey`,
        support: `/support`,
        betaProgram: `/pricing`,
        contact: `/contact`,
        profile: '/profile',
        profileDetails: '/profile/:userId',
        myEvents: '/my-events',
        tickets: '/my-tickets',
        network: '/network',
        networkSection: '/network/:section',
        partnerDashboard: '/partner-dashboard',
        communityFeed: '/community-feed',
        communityDetails: '/communities/:slug',
        pods: '/pods',
        podDetails: '/pods/:slug',
        privacyPolicyPage: '/privacy-policy',
        termsOfServicePage: '/terms-of-service',
        signupTermsPage: '/signup-terms',
        cookiePolicyPage: '/cookie-policy',
        accountDeletionPage: '/account-deletion',
        unsubscribe: '/unsubscribe',
        join: '/communities/:communitySlug/join',
    },
    dashboard: `${ROOTS.MANAGE}/dashboard`,
    reviewFormResponses: `${ROOTS.MANAGE}/dashboard?reviewResponses=unseen`,
    partnerDashboard: `${ROOTS.MANAGE}/partner-dashboard`,
    locations: {
        list: `${ROOTS.MANAGE}/locations`,
        details: `${ROOTS.MANAGE}/locations/:id`,
    },
    delegates: {
        list: `${ROOTS.MANAGE}/delegates`,
        approvals: `${ROOTS.MANAGE}/delegates/approvals`,
        details: `${ROOTS.MANAGE}/delegates/:id`,
    },
    events: {
        root: `${ROOTS.MANAGE}/events`,
        list: `${ROOTS.MANAGE}/events/list`,
        new: `${ROOTS.MANAGE}/events/new`,
        quick: `${ROOTS.MANAGE}/events/new/quick`,
        overview: `${ROOTS.MANAGE}/events/list/:id`, // Parent layout route
        edit: `${ROOTS.MANAGE}/events/list/:id/edit`,
        schedule_calendar: `${ROOTS.MANAGE}/events/schedule`,
        management: {
            overview: `${ROOTS.MANAGE}/events/list/:id/overview`,
            programme: {
                schedule: `${ROOTS.MANAGE}/events/list/:id/programme/schedule`,
                tasks: `${ROOTS.MANAGE}/events/list/:id/programme/tasks`,
                sponsorships: `${ROOTS.MANAGE}/events/list/:id/programme/sponsorships`,
            },
            people: {
                participants: `${ROOTS.MANAGE}/events/list/:id/people/participants`,
                attendees: `${ROOTS.MANAGE}/events/list/:id/people/attendees`,
                registrations: `${ROOTS.MANAGE}/events/list/:id/people/registrations`,
                analytics: `${ROOTS.MANAGE}/events/list/:id/people/analytics`,
            },
            tickets: {
                root: `${ROOTS.MANAGE}/events/list/:id/tickets`,
                types: `${ROOTS.MANAGE}/events/list/:id/tickets/types`,
                bundlesPromos: `${ROOTS.MANAGE}/events/list/:id/tickets/bundles-promos`,
                waitlist: `${ROOTS.MANAGE}/events/list/:id/tickets/waitlist`,
                registrationTerms: `${ROOTS.MANAGE}/events/list/:id/tickets/registration-terms`,
            },
            finances: {
                ticketSales: `${ROOTS.MANAGE}/events/list/:id/finances/ticket-sales`,
                refunds: `${ROOTS.MANAGE}/events/list/:id/finances/refunds`,
                locations: `${ROOTS.MANAGE}/events/list/:id/finances/locations`,
                staff: `${ROOTS.MANAGE}/events/list/:id/finances/staff`,
                expenses: `${ROOTS.MANAGE}/events/list/:id/finances/expenses`,
                reporting: `${ROOTS.MANAGE}/events/list/:id/finances/reporting`,
            },
            collaboration: {
                files: `${ROOTS.MANAGE}/events/list/:id/collaboration/files`,
                comments: `${ROOTS.MANAGE}/events/list/:id/collaboration/comments`,
                history: `${ROOTS.MANAGE}/events/list/:id/collaboration/history`,
            },
            outreach: {
                comms: `${ROOTS.MANAGE}/events/list/:id/outreach/comms`,
                campaigns: `${ROOTS.MANAGE}/events/list/:id/outreach/campaigns`,
                forms: `${ROOTS.MANAGE}/events/list/:id/outreach/forms`,
                invites: `${ROOTS.MANAGE}/events/list/:id/outreach/invites`,
                emailHistory: `${ROOTS.MANAGE}/events/list/:id/outreach/email-history`,
                eventViews: `${ROOTS.MANAGE}/events/list/:id/outreach/event-views`,
            },
            liveCheckin: `${ROOTS.MANAGE}/events/list/:id/live-checkin`,
        },
    },
    preview: {
        root: `${ROOTS.MANAGE}/events/preview`,
        events: `${ROOTS.MANAGE}/events/preview`, // Under events section
        details: `${ROOTS.MANAGE}/events/preview/:id`,
        register_root: `${ROOTS.MANAGE}/events/preview/:id/*`,
        register: `${ROOTS.MANAGE}/events/preview/:id/register`,
        payment: `${ROOTS.MANAGE}/events/preview/:id/payment`,
        confirmation: `${ROOTS.MANAGE}/events/preview/:id/confirmation`,
    },
    profile: `${ROOTS.MANAGE}/profile`,
    company: {
        root: `${ROOTS.MANAGE}/company`,
        profile: `${ROOTS.MANAGE}/company/profile`,
        subscription: `${ROOTS.MANAGE}/company/subscription`,
        account: `${ROOTS.MANAGE}/company/account`,
        transactions: `${ROOTS.MANAGE}/company/transactions`,
        registrationTemplates: `${ROOTS.MANAGE}/company/registration-templates`,
        sessionCustomisation: `${ROOTS.MANAGE}/company/session-customisation`,
        emailTemplates: `${ROOTS.MANAGE}/company/email-templates`,
        emailForms: `${ROOTS.MANAGE}/company/email-forms`,
        platformBranding: `${ROOTS.MANAGE}/company/platform-branding`,
        dataHealth: `${ROOTS.MANAGE}/company/data-health`,
        legal: `${ROOTS.MANAGE}/company/legal`,
    },
    users: `${ROOTS.MANAGE}/users`,
    tasks: `${ROOTS.MANAGE}/tasks`,
    finance: `${ROOTS.MANAGE}/finance`,
    financeRefunds: `${ROOTS.MANAGE}/finance/refunds`,
    communityHub: `${ROOTS.MANAGE}/community-hub`,
    communityHubCreate: `${ROOTS.MANAGE}/community-hub/create`,
    communityHubDetails: `${ROOTS.MANAGE}/community-hub/:communitySlug`,
    communityHubSettings: `${ROOTS.MANAGE}/community-hub/:communitySlug/settings`,
    communityHubCollaboratorInvite: `/communities/:communitySlug/collaborator-invite`,
    esg: `${ROOTS.MANAGE}/esg`,
    comms: `${ROOTS.MANAGE}/comms`,
    campaigns: `${ROOTS.MANAGE}/campaigns`,
    campaignsNew: `${ROOTS.MANAGE}/campaigns/new`,
    devOps: `${ROOTS.MANAGE}/devops`,
}